import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type DownloadStatus =
  | 'preparing'
  | 'processing'
  | 'downloading'
  | 'completed'
  | 'error';

interface DownloadFile {
  id: string;
  name: string;
  /** URL of the download job — polled until READY, never the presigned link. */
  url: string;
  progress: number;
  status: DownloadStatus;
  /** Error message from the backend, shown in the tray next to "Retry". */
  note?: string;
}

interface DownloadState {
  downloadingFiles: DownloadFile[];
}

const initialState: DownloadState = {
  downloadingFiles: [],
};

const downloadSlice = createSlice({
  name: 'download',
  initialState,
  reducers: {
    addDownload: (state, action: PayloadAction<DownloadFile>) => {
      state.downloadingFiles.push(action.payload);
    },
    addDownloads: (state, action: PayloadAction<DownloadFile[]>) => {
      state.downloadingFiles.push(...action.payload);
    },
    updateProgress: (
      state,
      action: PayloadAction<{ id: string; progress: number }>
    ) => {
      const file = state.downloadingFiles.find(f => f.id === action.payload.id);
      if (file) {
        file.progress = action.payload.progress;
      }
    },
    setStatus: (
      state,
      action: PayloadAction<{ id: string; status: DownloadStatus }>
    ) => {
      const file = state.downloadingFiles.find(f => f.id === action.payload.id);
      if (file) {
        file.status = action.payload.status;
        // Leaving a stale message behind would mislabel a retried download.
        if (action.payload.status !== 'error') {
          file.note = undefined;
        }
      }
    },
    setError: (
      state,
      action: PayloadAction<{ id: string; note?: string }>
    ) => {
      const file = state.downloadingFiles.find(f => f.id === action.payload.id);
      if (file) {
        file.status = 'error';
        file.note = action.payload.note;
      }
    },
    setName: (
      state,
      action: PayloadAction<{ id: string; name: string }>
    ) => {
      const file = state.downloadingFiles.find(f => f.id === action.payload.id);
      if (file) {
        file.name = action.payload.name;
      }
    },
    removeDownload: (state, action: PayloadAction<string>) => {
      state.downloadingFiles = state.downloadingFiles.filter(
        f => f.id !== action.payload
      );
    },
    clearAllDownloads: state => {
      state.downloadingFiles = [];
    },
  },
});

/**
 * True while a download is being prepared or handed over to the browser.
 *
 * Only one download runs at a time, so the download buttons watch this. Failed
 * entries stay in the tray until the user retries or dismisses them, and must
 * not keep the buttons locked.
 */
export const selectHasActiveDownload = (state: {
  download: DownloadState;
}): boolean =>
  state.download.downloadingFiles.some(file => file.status !== 'error');

export const {
  addDownload,
  addDownloads,
  updateProgress,
  setStatus,
  setError,
  setName,
  removeDownload,
  clearAllDownloads,
} = downloadSlice.actions;

export default downloadSlice.reducer;
